import { ref, markRaw, toRaw } from 'vue'
import { storeToRefs } from 'pinia'
import axios, { baseUrl } from '@/api'
import type { AxiosError } from 'axios'
import { Device } from '@twilio/voice-sdk'
import type { Call } from '@twilio/voice-sdk'
import type { Agent, Team, CallinglyCall, Lead, CallUser } from '@/types/types'
import * as Sentry from '@sentry/vue'
import { benignTwilioErrorCodes } from '@/sentry'
import { TelnyxDevice } from '@/lib/telnyxDevice'
import { toast } from 'vue-sonner'
import { version } from '../../package.json'
import { useAudioDevices } from '@/composables/useAudioDevices'
import { useDialer } from '@/composables/useDialer'
import { useTransfer } from '@/composables/useTransfer'
import { useUserStore } from '@/stores/user'
import { useSmsStore } from '@/stores/sms'
import { useStatusStore } from '@/stores/status'
import { useDeviceStore } from '@/stores/device'
import { useErrorsStore } from '@/stores/errors'
import { useOnlineStore } from '@/stores/online'
import { useRegisteredStore } from '@/stores/registered'
import { useConnectionStore } from '@/stores/connection'

// The /callusers/{id} response is a CallUser row with its parent call and the
// account's auto-accept flag embedded alongside it.
type IncomingCallUser = CallUser & { call: CallinglyCall; autoaccept: boolean }

// Module-level singleton state so App.vue and any consumer share the same
// reactive call-engine state (same pattern as useAudioDevices/useDialer/useTransfer).
const agent_id = ref<number | null>(null)
const call = ref<CallinglyCall | null>(null)
const call_user = ref<CallUser | null>(null)
const call_user_id = ref<number | null>(null)
const error_message = ref<string | null>(null)
const message = ref('Connecting...')
const note = ref<string>('')
const seconds = ref<number>(-1)
const timer = ref<number | null>(null)
const lead = ref<Partial<Lead>>({})
const isMuted = ref(false)
const isOnHold = ref<boolean>(false)
const isDialpadOpen = ref(false)
const isTransferOpen = ref(false)
const isTransferStarted = ref(false)
const isMakingCall = ref(false)
const openTab = ref<string | null>(null)
const showCallConfirm = ref(false)
const callConfirmLead = ref<Lead | null>(null)
const autoaccept = ref(false)
const twilioCall = ref<Call | null>(null)
const pingInterval = ref<number | null>(null)
const dispositionCountdown = ref<number>(50)
const dispositionTimer = ref<number | null>(null)
const DISPOSITION_SECONDS_TO_WAIT = 50
const signalingWatchdogTimer = ref<number | null>(null)
const signalingWatchdogAttempts = ref(0)
// Plain variable, not a ref: reactive wrapping breaks the object-identity
// comparison the watchdog relies on (proxy !== raw). Identity-only, hence
// `unknown` — the store's UnwrapRef<Device> isn't assignable to Device.
let signalingWatchdogDevice: unknown = null
const SIGNALING_WATCHDOG_MS = 45000
// After a 31206 (registration rate limit), suppress the unregistered handler's
// immediate rebuild until this timestamp — recovery comes from the watchdog's
// slower cadence instead. Plain number, no reactivity needed.
let registrationCooldownUntil = 0
const REGISTRATION_COOLDOWN_MS = 30000

// Every token request says this build can run on Telnyx (CAL-78). The backend
// answers `provider: telnyx` with a Telnyx login token when the rep's account
// rings its apps there, and a Twilio token otherwise.
const TOKEN_URL = '/api/v1/twilio/token?platform=desktop&supports_telnyx=1'

type TokenProvider = 'twilio' | 'telnyx'

const providerOf = (data: { provider?: string }): TokenProvider =>
  data.provider === 'telnyx' ? 'telnyx' : 'twilio'

// Set when a token refresh finds the account on the other provider while the
// rep is busy; the phone is rebuilt once they are back at the waiting screen.
let providerSwitchPending = false

//@ts-expect-error: Only available in electron
//we only need this code for the old electron app 1.2.1 and below
//after a few months of 1.4.0 we can remove this code
let ipcRenderer

if (/electron/i.test(navigator.userAgent)) {
  // `require` only exists in the legacy Electron (<= 1.2.1) build
  const legacyRequire = (window as unknown as { require?: (m: string) => { ipcRenderer: unknown } })
    .require
  if (legacyRequire) {
    ipcRenderer = legacyRequire('electron').ipcRenderer
  }
}

export function useCall() {
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  const statusStore = useStatusStore()
  const { status } = storeToRefs(statusStore)

  const deviceStore = useDeviceStore()
  const { device } = storeToRefs(deviceStore)

  const errorsStore = useErrorsStore()

  const onlineStore = useOnlineStore()
  const { online } = storeToRefs(onlineStore)

  const registeredStore = useRegisteredStore()
  const { registered } = storeToRefs(registeredStore)

  const connectionStore = useConnectionStore()

  const smsStore = useSmsStore()

  const { audio, updateAudioDevicesList, setAudioDevices } = useAudioDevices()
  const { fname, lname, phone_number, caller_id, getCallerIds } = useDialer()
  const { getAgents, getTeams } = useTransfer()

  // Ping the server every 60 seconds when user is logged in and device is registered
  const ping = async () => {
    try {
      await axios.post('/api/v1/user/ping', { device: 'app' })
    } catch (err) {
      console.error('Ping failed:', err)
    }
  }

  // Notify the server when the user disconnects (logout or app close)
  const disconnect = () => {
    if (!user.value?.id) return
    navigator.sendBeacon(
      `${baseUrl}/api/v1/user/disconnect`,
      new Blob([JSON.stringify({ device: 'app' })], { type: 'application/json' }),
    )
  }

  const startPingInterval = () => {
    if (pingInterval.value) return
    ping() // Ping immediately
    pingInterval.value = window.setInterval(ping, 60000)
  }

  const stopPingInterval = () => {
    if (pingInterval.value) {
      clearInterval(pingInterval.value)
      pingInterval.value = null
    }
  }

  const startPhone = async (attempt = 1) => {
    const maxAttempts = 3
    let token = null
    let provider: TokenProvider = 'twilio'

    try {
      console.log('startPhone Fetching token...')

      const response = await axios.get(TOKEN_URL)

      if (!response.data?.token) {
        Sentry.captureException(new Error('startPhone Invalid token received'))
        return
      }

      token = response.data.token
      provider = providerOf(response.data)
      console.log('startPhone Token received')
    } catch (error) {
      // A 422 means the account has no Twilio configured — retrying won't help,
      // so surface a clear message and stop instead of leaving a dead phone.
      // (A 401 is handled globally by the api interceptor, which reloads to log in.)
      if ((error as AxiosError).response?.status === 422) {
        toast.error('Calling is not set up for your account. Please contact support.')
        return
      }

      if (attempt < maxAttempts) {
        console.error(
          `startPhone Attempt ${attempt} of ${maxAttempts} failed to fetch token. Retrying in 10 seconds...`,
        )
        window.setTimeout(() => {
          startPhone(attempt + 1)
        }, 10000)
      } else {
        console.error(
          `startPhone Attempt ${attempt} of ${maxAttempts} failed to fetch token. All retry attempts exhausted.`,
        )
        //Sentry.captureException(error)
      }

      return
    }

    // TelnyxDevice stands in for Twilio's Device: it has the members this
    // composable, useAudioDevices and the pusher store use, with the same
    // states, events and error codes (see lib/telnyxDevice).
    // A fresh token names the account's current provider.
    providerSwitchPending = false
    device.value = markRaw(
      provider === 'telnyx'
        ? (new TelnyxDevice(token, {
            ringtoneFile: 'https://cdn.callingly.com/mp3s/us-ring2.mp3',
            debug: user.value?.account_id === 1,
          }) as unknown as Device)
        : new Device(token, {
            // 0 = trace, 1 = debug, 2 = info, 3 = warn, 4 = error, 5 = silent
            logLevel: user.value?.account_id === 1 ? 1 : 4,
            sounds: {
              incoming: 'https://cdn.callingly.com/mp3s/us-ring2.mp3',
            },
            enableImprovedSignalingErrorPrecision: true,
            // With the default (0), losing the signaling websocket mid-call tears the
            // call down immediately (the 31005 storms in Sentry — agent hears dead air
            // while the lead is still on the line). 30s lets the SDK re-attach the call
            // when signaling reconnects; the 'reconnecting'/'reconnected' handlers
            // already show the banner while it recovers. Twilio holds terminal status
            // callbacks for up to this long when signaling is lost mid-call.
            maxCallSignalingTimeoutMs: 30000,
          }),
    )

    setupDeviceHandlers()

    // register() resolves on the 'registered' event and rejects when registration
    // fails (e.g. an invalid or expired token, or no transport). Left unhandled, that
    // rejection surfaces in Sentry as "Non-Error promise rejection captured with value:
    // undefined". The device 'error' handler wired in setupDeviceHandlers() already
    // reacts to these failures (refresh/destroy/restart), so swallow the rejection here
    // to avoid the duplicate unhandled-rejection noise.
    device.value.register().catch((error) => {
      console.error('Device registration failed:', error)
    })
    device.value?.audio?.disconnect(false)
  }

  const refreshtoken = async (attempt = 1) => {
    const maxAttempts = 3

    console.log('refreshtoken Refreshing token...')

    try {
      const response = await axios.get(TOKEN_URL)

      if (!response.data?.token) {
        Sentry.captureException(new Error('refreshtoken Invalid token received'))

        return
      }

      // The account moved between Twilio and Telnyx since the phone started:
      // a token from one can't be handed to the other's device.
      if (device.value && providerOf(response.data) !== currentProvider()) {
        providerSwitchPending = true
        restartForProviderSwitch()

        return
      }

      device.value?.updateToken(response.data.token)
      console.log('refreshtoken Token refreshed')

      // updateToken() is passive — a dormant device won't re-register just
      // because its token changed. If we're idle, online, and cleanly
      // unregistered (e.g. a post-call recovery whose register() raced a stale
      // token, or a delayed retry of this function landing after one), register
      // now, so every refresh path converges on a working phone.
      if (
        online.value &&
        device.value?.state === Device.State.Unregistered &&
        (!twilioCall.value || twilioCall.value.status() === 'closed')
      ) {
        console.log('refreshtoken Device unregistered after refresh — registering...')
        device.value.register().catch((registerError) => {
          console.error('Post-refresh register failed:', registerError)
        })
      }
    } catch (error) {
      // A 422 means the account has no Twilio configured — retrying won't help,
      // so surface a clear message and stop instead of leaving a dead phone.
      // (A 401 is handled globally by the api interceptor, which reloads to log in.)
      if ((error as AxiosError).response?.status === 422) {
        toast.error('Calling is not set up for your account. Please contact support.')
        return
      }

      if (attempt < maxAttempts) {
        console.error(
          `refreshtoken Attempt ${attempt} of ${maxAttempts} failed to refresh token. Retrying in 10 seconds...`,
        )
        window.setTimeout(() => {
          refreshtoken(attempt + 1)
        }, 10000)
      } else {
        console.error(
          `refreshtoken Attempt ${attempt} of ${maxAttempts} failed to refresh token. All retry attempts exhausted.`,
        )
        //Sentry.captureException(error)
      }

      return
    }
  }

  // Rebuild the phone on the account's new provider, but only with the rep
  // idle at the waiting screen: the rebuild's reset() would throw away a live
  // call, a dial being set up, or an unfinished disposition or note. reset()
  // checks back, so the rebuild happens as the rep finishes. destroy() emits
  // 'unregistered', and that handler starts the phone again.
  const restartForProviderSwitch = () => {
    const isCallOngoing = !!twilioCall.value && twilioCall.value.status() !== 'closed'

    if (
      !providerSwitchPending ||
      !device.value ||
      isCallOngoing ||
      isMakingCall.value ||
      status.value !== 'waiting'
    ) {
      return
    }

    providerSwitchPending = false
    console.log(`Calls moved off ${currentProvider()} — restarting phone`)
    device.value.destroy()
  }

  const currentProvider = (): TokenProvider =>
    (toRaw(device.value) as unknown) instanceof TelnyxDevice ? 'telnyx' : 'twilio'

  const getAudioPermissions = async () => {
    // Call getUserMedia to ask for device permission and populate the device labels.
    // Also, performing this action here allows for capturing gUM (getUserMedia) errors early
    // before accepting/receiving a call and it's possible to create a much better user experience.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Calling getUserMedia will start the media track selected.
      // This is not desired as the user may get the impression the mic is in use.
      // Therefore, we want to avoid having tracks started when they're not needed.
      // We only wanted to get the input device list so we stop the tracks immediately.
      stream.getTracks().forEach((track) => track.stop())
    } catch (error) {
      // A denied or missing microphone must not throw here: the caller awaits
      // this during startup (App.vue onMounted) before registering the phone, so
      // an unhandled rejection would abort the rest of init (the device never
      // registers). Surface a clear, actionable message and continue; mic errors
      // at call time are still handled by the device 'error' handler.
      const name = (error as DOMException)?.name

      if (name === 'NotAllowedError') {
        toast.error(
          'Microphone access is blocked. Enable it in your browser settings to make calls.',
        )
      } else if (name === 'NotFoundError') {
        toast.error('No microphone found. Connect a microphone to make calls.')
      } else {
        console.error('getAudioPermissions failed:', error)
      }
    }
  }

  // Watchdog for the zombie-device state. After a recoverable signaling error
  // (31000/31005/31009) the SDK usually restores the websocket and re-registers
  // on its own — but it can wedge in 'registering' with a dead transport for
  // minutes (Sentry CALLINGLY-PHONE-AK/AM capture devices with state
  // "registering", stream "disconnected"). In that state 'unregistered' never
  // fires, navigator.onLine looks fine, and register() throws (only legal from
  // 'unregistered'), so no existing handler recovers: the agent looks online
  // while invites to this client silently fail. Check back after each
  // recoverable error; nudge a cleanly unregistered device via refreshtoken()
  // (whose success path re-registers), give a wedged one a grace period, then
  // rebuild via destroy() — its synthesized 'unregistered' event re-runs
  // startPhone(). Deliberately gentle (45s steps, one nudge before rebuilding,
  // never under a live call or the disposition screen) so it doesn't fight the
  // SDK's own backoff or trip Twilio's registration rate limit (31206).
  const disarmSignalingWatchdog = () => {
    if (signalingWatchdogTimer.value) {
      clearTimeout(signalingWatchdogTimer.value)
      signalingWatchdogTimer.value = null
    }
    signalingWatchdogAttempts.value = 0
    signalingWatchdogDevice = null
  }

  const armSignalingWatchdog = () => {
    if (!device.value) return

    // Bind the watchdog to the device it was armed for: if another lifecycle
    // path replaces the device before it fires, the stale attempt schedule
    // must not carry over to the fresh device (which is legitimately still
    // registering). A pending watchdog for the CURRENT device is left alone;
    // one for a replaced device is restarted here so the fresh device's error
    // doesn't get swallowed by the old timer.
    const current = toRaw(device.value)
    if (signalingWatchdogTimer.value) {
      if (signalingWatchdogDevice === current) return
      disarmSignalingWatchdog()
    }
    signalingWatchdogDevice = current

    signalingWatchdogTimer.value = window.setTimeout(() => {
      signalingWatchdogTimer.value = null

      const state = device.value?.state

      // Recovered, deliberately torn down, replaced by a newer device, or
      // offline (the online handler owns that path) — stand down.
      if (
        !device.value ||
        toRaw(device.value) !== signalingWatchdogDevice ||
        state === Device.State.Registered ||
        state === Device.State.Destroyed ||
        !online.value
      ) {
        disarmSignalingWatchdog()
        return
      }

      // Never rebuild under a live call, mid outbound setup (status is still
      // 'waiting' and twilioCall still null while makeOutboundCall awaits the
      // call context and device.connect()), and only when the agent is idle at
      // the waiting screen — the rebuild's reset() would discard an unfinished
      // disposition, note, or any other post-call flow. Check back later.
      const isCallOngoing = !!twilioCall.value && twilioCall.value.status() !== 'closed'
      if (isCallOngoing || isMakingCall.value || status.value !== 'waiting') {
        armSignalingWatchdog()
        return
      }

      signalingWatchdogAttempts.value++
      console.warn(
        `Signaling watchdog: device "${state}" ${SIGNALING_WATCHDOG_MS / 1000}s after a recoverable error (attempt ${signalingWatchdogAttempts.value})`,
      )

      if (signalingWatchdogAttempts.value === 1) {
        if (state === Device.State.Unregistered) {
          // refreshtoken() re-registers an idle unregistered device on success.
          refreshtoken()
        }
        // 'registering': the SDK may still connect — one more grace period.
        armSignalingWatchdog()
        return
      }

      const stuckDevice = device.value
      disarmSignalingWatchdog()
      Sentry.captureMessage(`Signaling watchdog rebuilding device stuck in "${state}"`, 'warning')
      // destroy() emits 'unregistered' on the way down; that handler restarts
      // the phone with a fresh token (same pattern as the 20101/20104 paths).
      stuckDevice.destroy()
    }, SIGNALING_WATCHDOG_MS)
  }

  const setupDeviceHandlers = () => {
    if (!device.value) {
      console.error('setupDeviceHandlers failed! No device value.')
      return
    }

    console.log('Setting device handlers...')

    // The device these handlers were wired to. Replaced devices keep their
    // handlers, so events can arrive from a device that is no longer
    // device.value — anything acting on the current device (like the
    // signaling watchdog) must first check the emitter is still current.
    const boundDevice = toRaw(device.value)

    device.value.setMaxListeners(5)

    device.value.on('registered', () => {
      // A replaced device's delayed event must not mutate the current device's
      // readiness, cancel its watchdog, or reset the current workflow.
      if (toRaw(device.value) !== boundDevice) return

      console.log('Device registered')
      registered.value = true
      registrationCooldownUntil = 0
      disarmSignalingWatchdog()
      updateAudioDevicesList()
      reset()
    })

    // We want to detect if the device list changes e.g. a headset was plugged in/out.
    // We set up handlers to update our dropdown list with the new device list
    // Subscribe to the event for when the list of devices changes
    device.value.audio?.on('deviceChange', () => updateAudioDevicesList())

    device.value.on('unregistered', function () {
      console.log('Device unregistered')
      registered.value = false
      disconnect()

      // Rate-limited (31206): an immediate rebuild here is what fuels the
      // storm loop — register, 31206, unregistered, register again. Sit out
      // the cooldown; the signaling watchdog armed by the 31206 branch retries
      // on its slower cadence.
      if (Date.now() < registrationCooldownUntil) {
        console.warn('Skipping immediate phone restart — registration cooldown active')
        return
      }

      if (user.value && online.value) {
        reset()
        startPhone()
      }
    })

    device.value.on('error', async function (error) {
      errorsStore.addError(error)

      // Whether a call was live when this arrived. Read it up front: most
      // branches below call reset(), which clears twilioCall, and the
      // "is this noise or a dropped call?" decisions all depend on it.
      const isCallOngoing = !!twilioCall.value && twilioCall.value.status() !== 'closed'

      // 31000: General Twilio Voice SDK error.
      // 31005: WebSocket connection to Twilio's signaling servers was unexpectedly ended.
      // 31009: No transport available to send or receive messages.
      // The SDK recovers the signaling connection on its own, so don't tear
      // anything down — but don't swallow them silently either: mid-call they
      // explain dropped calls. Idle, they're routine (every laptop sleep, wifi
      // switch and VPN flap trips one) so they'd swamp Sentry as their own
      // events; leave a breadcrumb instead, which still rides along on whatever
      // real error comes next.
      if (error.code == 31000 || error.code == 31005 || error.code == 31009) {
        console.warn(`Recoverable signaling error ${error.code}`, { isCallOngoing })

        // …but "recovers on its own" isn't guaranteed — see the watchdog.
        // Only for the current device: a replaced device's retained handler
        // must not arm a watchdog against its successor.
        if (toRaw(device.value) === boundDevice) {
          armSignalingWatchdog()
        }

        if (isCallOngoing) {
          Sentry.captureException(error)
        } else {
          Sentry.addBreadcrumb({
            category: 'twilio',
            level: 'warning',
            message: `Recoverable signaling error ${error.code} while idle`,
          })
        }
        return
      }

      // 31206: Twilio is rate-limiting our registration attempts. The worst
      // response is another immediate register cycle: before this branch the
      // generic handler reset() the UI and the 'unregistered' handler kicked
      // off startPhone() right away — fresh token, register, 31206 again — a
      // tight storm loop (hundreds of events in a minute in Sentry). Instead:
      // no reset, no toast, start a cooldown that the 'unregistered' handler
      // respects, and let the watchdog's 45s cadence (outside the cooldown)
      // make the single paced retry. The SDK's own transport backoff honors
      // the server's Retry-After on top of this.
      if (error.code == 31206) {
        // A replaced device's delayed 31206 must not set the shared cooldown:
        // it would suppress the current device's unregistered recovery while
        // the identity-guarded watchdog below never arms a retry for it.
        if (toRaw(device.value) !== boundDevice) return

        console.warn('Registration rate-limited (31206) — cooling down before re-registering')
        registrationCooldownUntil = Date.now() + REGISTRATION_COOLDOWN_MS

        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'warning',
          message: 'Registration rate-limited (31206), cooling down',
        })

        armSignalingWatchdog()
        return
      }

      reset()
      // 31401 PermissionDenied / 31402 AcquisitionFailed: the browser, OS or
      // hardware wouldn't hand over the mic. A user-environment state, already
      // surfaced to the rep here — breadcrumb it rather than reporting a Sentry
      // issue per rep machine (it otherwise falls through to the capture below).
      if (error.code == 31401 || error.code == 31402) {
        toast.error('Callingly is not able to access your microphone.')
        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'warning',
          message: `Microphone unavailable ${error.code}`,
        })
        return
      }

      // 31205: ERROR: JWT Token Expired
      if (error.code == 31205) {
        console.log('Error 31205: JWT Token Expired')
        Sentry.captureException(error)
        await refreshtoken()

        // 20101: ERROR: Invalid Access Token
      } else if (error.code == 20101) {
        console.log('Error 20101:  Invalid Access Token')
        //Sentry.captureException(error)

        // destroy the device to force a new token to be fetched
        if (registered.value) {
          console.log('Device registered... Destroying device...')
          if (device.value) {
            device.value.destroy()
          }
        } else {
          console.log('Device not registered... Resetting and starting phone...')
          reset()
          startPhone()
        }

        // 20104: WARNING: Access Token expired or expiration date invalid
      } else if (error.code == 20104) {
        console.log('Error 20104: Access Token expired or expiration date invalid')
        //Sentry.captureException(error)

        // destroy the device to force a new token to be fetched
        if (registered.value) {
          console.log('Device registered... Destroying device...')
          if (device.value) {
            device.value.destroy()
          }
        } else {
          console.log('Device not registered... Resetting and starting phone...')
          reset()
          startPhone()
        }
      } else if (benignTwilioErrorCodes.has(error.code) && !isCallOngoing) {
        // Expected signaling condition (rate limit, reconnect race, media path
        // failure) with nothing live to break. The SDK recovers on its own; a
        // breadcrumb keeps the context without each one becoming its own Sentry
        // issue. Same rule as 31000/31005/31009 above — if a call *was* up,
        // this error just killed it (reset() ran) and it's worth an event.
        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'warning',
          message: `Expected signaling error ${error.code} while idle`,
        })
        toast.error(`Device error. Code: ${error.code}`)
      } else {
        Sentry.captureException(error)
        toast.error(`Device error. Code: ${error.code}`)
        //throw error
      }
    })

    device.value.on('tokenWillExpire', async () => {
      await refreshtoken()
    })

    device.value.on('incoming', function (_call: Call) {
      console.log('Incoming call', twilioCall.value?.parameters.From)
      twilioCall.value = markRaw(_call)
      setupCallHandlers()
      incomingLead(Number(twilioCall.value.parameters.From.replace('client:', '')))
    })
  }

  const reset = () => {
    if (timer.value) {
      clearInterval(timer.value)
    }
    if (dispositionTimer.value) {
      clearInterval(dispositionTimer.value)
      dispositionTimer.value = null
    }
    message.value = ''

    //call.value = null
    //call_user.value = null

    isMuted.value = false
    isOnHold.value = false
    isDialpadOpen.value = false
    isTransferOpen.value = false
    isTransferStarted.value = false
    status.value = 'waiting'
    lead.value = {}
    connectionStore.resetCallHealth()

    // Outside this call stack: reset() runs inside other recovery paths,
    // which mustn't have the device destroyed under them.
    if (providerSwitchPending) {
      window.setTimeout(restartForProviderSwitch, 0)
    }
  }

  const handleOnlineStatusChange = () => {
    online.value = navigator.onLine

    if (!online.value) {
      console.log('Network offline. Checking in 10 seconds if still offline...')

      window.setTimeout(() => {
        if (!online.value) {
          // With a call up, leave the device alone so the SDK's 30s signaling
          // recovery window (maxCallSignalingTimeoutMs) can re-attach the call
          // when the network comes back. Destroying here would tear the call
          // down at 10s and defeat that window. If the outage outlasts it, the
          // SDK disconnects the call itself, and the online branch below
          // rebuilds the phone once connectivity returns.
          if (twilioCall.value && twilioCall.value.status() !== 'closed') {
            console.log(
              'Network offline with an active call — deferring device teardown to signaling recovery.',
            )
            return
          }

          console.log('Network offline after timeout. Destroying device...')
          reset()
          if (device.value) {
            device.value.destroy()
          }
        } else {
          console.log('Network online after timeout. Nothing to be done.')
        }
      }, 10000)
    } else {
      console.log('Network online')

      if (!registered.value) {
        // Same deferral as the offline branch: a rebuild here would replace the
        // device out from under a call the SDK is still re-attaching. If
        // recovery fails the SDK ends the call, and the next online/unregistered
        // signal lands in the rebuild path below with no call active.
        if (twilioCall.value && twilioCall.value.status() !== 'closed') {
          console.log(
            'Network online with an active call — letting signaling recovery re-register.',
          )
          return
        }

        console.log('Device not registered... Resetting and starting phone...')
        reset()
        startPhone()
      }
    }
  }

  // Call handling methods
  const acceptCall = () => {
    if (!twilioCall.value) {
      console.error('acceptCall failed! No twilioCall value.')
      return
    }
    if (twilioCall.value.direction !== 'OUTGOING') {
      setAudioDevices()
      twilioCall.value.accept()
    }

    if (
      call_user.value?.type === 'listen' ||
      call_user.value?.type === 'whisper' ||
      call_user.value?.type === 'barge'
    ) {
      seconds.value = call.value?.seconds_since_started_at ?? -1
    } else {
      seconds.value = -1
    }

    timerFunction()
    if (timer.value) {
      clearInterval(timer.value)
    }
    timer.value = window.setInterval(timerFunction, 1000)
    status.value = 'oncall'
  }

  const reject = async () => {
    if (timer.value) {
      clearInterval(timer.value)
    }

    // Stop the live ring and reset first, so Ignore never depends on the backend
    // request. A just-arrived call's details may still be loading, leaving
    // call.value absent (it's cleared on each incoming until the fetch resolves)
    // — so only notify the backend when we have this call's id, and never let
    // that request block or undo the local reject (awaiting it first used to
    // leave the call ringing when the post failed, or POST /webrtc/undefined).
    const callId = call.value?.id
    twilioCall.value?.reject()
    reset()

    if (callId) {
      try {
        await axios.post(`/apps/webrtc/${callId}/reject`)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }

  const hangup = () => {
    if (twilioCall.value?.status() !== 'open') {
      reset()
    }
    isTransferOpen.value = false
    twilioCall.value?.disconnect()
  }

  const mute = () => {
    isMuted.value = true
    twilioCall.value?.mute(true)
  }

  const unmute = () => {
    isMuted.value = false
    twilioCall.value?.mute(false)
  }

  const digitPress = (digit: string) => {
    twilioCall.value?.sendDigits(digit)
  }

  // UI interaction methods
  const goHome = () => {
    smsStore.clearCurrentConversation()
    smsStore.lastViewedConversationId = null
    smsStore.lastViewedConversationIndex = null
    smsStore.lastScrollPosition = 0
    openTab.value = null
    isDialpadOpen.value = false
    isTransferOpen.value = false
  }

  const toggleHistory = () => {
    openTab.value = 'history'
  }

  const toggleSms = () => {
    openTab.value = 'sms'
  }

  const toggleNewCall = (lead?: Lead, number?: string) => {
    // Re-tapping the already-open New Call tab is a no-op so typed input isn't wiped
    if (!lead && !number && openTab.value === 'new_call') {
      return
    }

    error_message.value = null
    if (lead) {
      fname.value = lead.fname
      lname.value = lead.lname
      phone_number.value = lead.phone_number
    } else if (number) {
      fname.value = null
      lname.value = null

      // number already includes the country code so just use what we have
      if (number.includes('+')) {
        phone_number.value = number
      } else {
        // add users country code if available
        if (user.value?.country_dial_code) {
          phone_number.value = '+' + user.value?.country_dial_code + number
        } else {
          // otherwise add the default country code from the vue-tel-input
          phone_number.value = phone_number.value + number
        }
      }
    } else {
      fname.value = null
      lname.value = null
      phone_number.value = null
    }

    openTab.value = 'new_call'
  }

  const confirmCallLead = (lead: Lead) => {
    callConfirmLead.value = lead
    error_message.value = null
    showCallConfirm.value = true
  }

  const cancelCallConfirm = () => {
    showCallConfirm.value = false
    callConfirmLead.value = null
    error_message.value = null
  }

  const confirmAndCall = async () => {
    if (!callConfirmLead.value) return

    fname.value = callConfirmLead.value.fname
    lname.value = callConfirmLead.value.lname
    phone_number.value = callConfirmLead.value.phone_number
    openTab.value = 'new_call'

    await makeOutboundCall()

    if (!error_message.value) {
      showCallConfirm.value = false
      callConfirmLead.value = null
    }
  }

  const toggleDialpad = () => {
    isDialpadOpen.value = !isDialpadOpen.value

    if (isDialpadOpen.value) {
      isTransferOpen.value = false
    }
  }

  const toggleTransfer = () => {
    isTransferOpen.value = !isTransferOpen.value
    if (isTransferOpen.value) {
      getTeams()
      getAgents()
      isDialpadOpen.value = false
    }
  }

  const timerFunction = () => {
    seconds.value++

    const min = Math.floor(seconds.value / 60)
    const sec = seconds.value - min * 60

    const formattedMin = min < 10 ? `0${min}` : min
    const formattedSec = sec < 10 ? `0${sec}` : sec

    message.value = `${formattedMin}:${formattedSec}`
  }

  // Conference and transfer methods
  const startConference = async (agent: Agent) => {
    const response = await axios.post('/api/v1/calls/conference', {
      user_id: agent.id,
    })

    if (response.data.success) {
      openTab.value = null
      lead.value = {
        name: 'Conference',
      }
    }
  }

  const startTransfer = async (agent: Agent | null, team: Team | null) => {
    const request = agent
      ? {
          user_id: agent?.id,
          call_user_id: call_user_id.value,
        }
      : {
          team_id: team?.id,
          call_user_id: call_user_id.value,
        }

    if (agent) {
      agent.on_call_id = 1
    }

    const response = await axios.post(`/api/v1/calls/${call.value?.id}/transfer`, request)
    if (response.data.success) {
      isTransferOpen.value = false
      isTransferStarted.value = true
      // When the account has opted into holding the lead during warm transfers,
      // the backend parks the lead on hold so the two agents can talk privately
      // and reports it back as lead_on_hold. Reflect that here; the agent brings
      // the lead back in with the "Bring in Lead" control (unhold). Only write on
      // the truthy path so a lead that was already held isn't silently cleared
      // when the setting is off; when off the transfer merges as before.
      if (response.data.lead_on_hold) {
        isOnHold.value = true
      }
    }
  }

  const startListen = async (callId: number) => {
    const response = await axios.post(`/api/v1/calls/${callId}/listen`)
    if (response.data.success) {
      openTab.value = null
    }
  }

  const startWhisper = async (callId: number) => {
    const response = await axios.post(`/api/v1/calls/${callId}/whisper`)
    if (response.data.success) {
      openTab.value = null
    }
  }

  const startBarge = async (callId: number) => {
    const response = await axios.post(`/api/v1/calls/${callId}/barge`)
    if (response.data.success) {
      openTab.value = null
    }
  }

  const hold = () => {
    if (isOnHold.value) {
      return
    }

    isOnHold.value = true

    axios
      .put(`/api/v1/calls/${call.value?.id}/hold`)
      .then(() => {
        isOnHold.value = true
      })
      .catch((err) => {
        isOnHold.value = true
        console.error(err)
      })
  }

  const unhold = () => {
    if (!isOnHold.value) {
      return
    }

    isOnHold.value = false

    axios
      .put(`/api/v1/calls/${call.value?.id}/unhold`)
      .then(() => {
        isOnHold.value = false
      })
      .catch((err) => {
        isOnHold.value = true
        console.error(err)
      })
  }

  // Disposition methods
  const thankyou = () => {
    status.value = 'thankyou'
  }

  const disposition = async (dispositionStatus: string) => {
    if (call.value?.id) {
      try {
        await axios.post(`/apps/webrtc/${call.value?.id}/disposition`, {
          status: dispositionStatus,
          version: version,
          seconds_remaining: dispositionCountdown.value,
        })
      } catch (err) {
        if ((err as AxiosError).response?.status === 422) {
          toast.error('This call has already been finalized and cannot be updated.')
        } else {
          toast.error('Failed to save call result. Please try again.')
        }
        return
      }
    } else {
      Sentry.captureException(new Error('Disposition with no call id'))
    }

    // Every result goes on to the lead's tags & stage; the editor needs a call.
    if (call.value?.id) {
      status.value = 'disposition2'
    } else {
      thankyou()
    }
  }

  const saveNote = async () => {
    await axios.post(`/api/v1/notes`, { content: note.value, call_id: call.value?.id })
    note.value = ''
    reset()
  }

  const skipNote = async () => {
    note.value = ''
    reset()
  }

  // Retry methods
  const retry = async () => {
    if (!lead.value) return

    const response = await axios.post('/api/v1/calls/twilio', {
      fname: lead.value.fname,
      lname: lead.value.lname,
      phone_number: lead.value.phone_number,
    })

    if (response.data.success) {
      status.value = 'waiting'
      lead.value = response.data.call.lead
    } else {
      error_message.value = response.data.error
    }
  }

  // Conference lead methods
  const fetchCallUser = async (callUserId: number): Promise<IncomingCallUser> => {
    console.log('Getting Callingly Call with callUserId', callUserId)
    const response = await axios.get(`/api/v1/callusers/${callUserId}`)
    return response.data
  }

  const applyCallUser = (callUserId: number, data: IncomingCallUser) => {
    call.value = data.call
    call_user.value = data
    call_user_id.value = callUserId
    autoaccept.value = data.autoaccept
  }

  const getCallinglyCall = async (callUserId: number) => {
    applyCallUser(callUserId, await fetchCallUser(callUserId))
  }

  const clearLoadedCall = () => {
    call.value = null
    call_user.value = null
    lead.value = {}
  }

  const incomingLead = async (callUserId: number) => {
    console.log('Incoming lead with callUserId', callUserId)

    // close the open tab if it's open
    if (openTab.value) {
      openTab.value = null
    }

    // The call this invocation belongs to. During the awaited details fetch the
    // caller can cancel, the agent can ignore, or a newer call can arrive — a
    // cancel/ignore closes this call (and reset() leaves twilioCall pointing at
    // it), a new call replaces twilioCall. The continuation after the await must
    // not mutate shared state or auto-accept unless it still owns this same,
    // live call, or a stale lookup would auto-accept a dead call or clobber a
    // newer one's metadata.
    const incomingCall = twilioCall.value
    const ownsIncomingCall = () =>
      twilioCall.value === incomingCall && incomingCall?.status() !== 'closed'

    // Show the ringing screen immediately, before fetching the call details.
    // The Twilio Device is already ringing audibly by the time this fires (the
    // SDK plays sounds.incoming on the 'incoming' event) and acceptCall() only
    // needs twilioCall — so the agent must be able to pick up even if the
    // details fetch below is slow or fails. Gating the Accept card on that fetch
    // is what left agents hearing a ring with no way to answer when
    // GET /callusers/{id} errored (e.g. a lapsed web session 401ing, or a 404
    // race before the row was readable).
    message.value = 'Incoming call!'
    status.value = 'ringing'

    // We don't know this call's details yet; clear anything left over from a
    // prior call (reset() intentionally keeps call/call_user) so nothing acts on
    // stale metadata while the fetch is in flight — neither the lead card nor
    // Ignore, which rejects the backend call by call.value.id.
    clearLoadedCall()

    if (/electron/i.test(navigator.userAgent)) {
      //@ts-expect-error: Only available in electron
      if (ipcRenderer) {
        //we only need this code for the old electron app 1.2.1 and below
        //after a few months of 1.4.0 we can remove this code
        ipcRenderer.invoke('incoming-call')
      } else {
        //@ts-expect-error: Only available in electron
        // this is the new electron app 1.4.0 and above
        window.electronAPI?.incomingCall()
      }
    }

    // getCallinglyCall can miss on a transient race — the call_user row gets
    // dialed a beat before it's readable (404), or a brief network/5xx blip —
    // so retry a few times with a short delay to still populate the lead card
    // while the call rings. A hard failure isn't retried: 401 is handled
    // globally by the api interceptor (reload to login), 403/422 won't recover.
    // Bail the moment we no longer own the call so a canceled ring doesn't spin.
    const maxAttempts = 3
    let gotDetails = false

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Fetch without touching shared state, then bail if the call ended or
        // was replaced while the request was in flight — otherwise a stale
        // lookup would overwrite the live call's metadata (call, call_user,
        // autoaccept) that accept/reject/transfer/completion later rely on.
        const data = await fetchCallUser(callUserId)
        if (!ownsIncomingCall()) {
          return
        }
        applyCallUser(callUserId, data)
        gotDetails = true
        break
      } catch (error) {
        if (!ownsIncomingCall()) {
          return
        }

        const httpStatus = (error as AxiosError).response?.status
        const isTransient = httpStatus === undefined || httpStatus === 404 || httpStatus >= 500

        if (!isTransient || attempt === maxAttempts) {
          Sentry.captureException(error)
          break
        }

        await new Promise((resolve) => window.setTimeout(resolve, 400 * attempt))
      }
    }

    // The call ended or was replaced while the details were in flight; don't
    // label, and above all don't auto-accept, a call we no longer own.
    if (!ownsIncomingCall()) {
      return
    }

    if (!gotDetails) {
      // Every retry failed. Stay on the ringing screen so the agent can still
      // accept; call/call_user/lead were already cleared above, so there is no
      // stale lead to mislabel this call with.
      return
    }

    if (call.value?.type === 'conference') {
      lead.value = {
        name: 'Conference',
      }
    } else {
      if (call.value?.lead) {
        lead.value = call.value.lead
      }
    }

    if (call.value?.direction == 'inbound') {
      message.value = call.value.number.name

      if (call.value.number.account_id == 9420) {
        message.value = `${call.value.number.name} - ${call.value.number.phone_number_formatted}`
      }
    } else if (call.value?.type == 'transfer') {
      message.value = 'Incoming Transfer!'
    } else if (call.value?.type == 'conference') {
      message.value = 'Incoming Conference!'
    } else {
      message.value = 'Incoming Lead!'
    }

    if (call.value?.account_id == 10787 || call.value?.account_id == 3998) {
      acceptCall()
    }

    if (autoaccept.value) {
      acceptCall()
    }
  }

  // Carries its own timeout because the shared axios client sets none, and the
  // failure that brings us here is usually a network one — without it the
  // request can stay pending indefinitely.
  const ABANDON_TIMEOUT_MS = 10000

  // Release a call the server created for a dial whose Twilio leg never came
  // up. dial() has to create it before device.connect() can run, because
  // connect() needs the call_user_id it returns, and nothing else rolls it
  // back: the row sits in 'ringing' with no call_sid until MinuteQueries'
  // 2-minute sweep marks it missed and emails "App Call Stuck Ringing". The
  // endpoint re-checks those same conditions, so a leg that did come up after
  // all is never torn down by a late abandon.
  const abandonOutboundCall = async (callId: number) => {
    try {
      await axios.post(`/api/v1/calls/${callId}/abandon`, undefined, {
        timeout: ABANDON_TIMEOUT_MS,
      })
    } catch (err) {
      // Best-effort: the sweep is still there as the backstop, and the rep has
      // already been told the dial failed.
      console.error('Failed to release the abandoned call', err)
    }
  }

  // Make outbound call
  const makeOutboundCall = async () => {
    if (!online.value || !registered.value) {
      return
    }

    // registered is a cached flag, and the device 'error' handler deliberately
    // leaves it true through the recoverable signaling errors (31000/31005/
    // 31009) — so it still reads ready while the websocket is dead and the
    // device sits wedged in 'registering' (the state the signaling watchdog
    // rebuilds). Dialing on that flag alone is what strands calls in 'ringing':
    // the server creates the call, connect() never reaches Twilio, and nothing
    // says so. Ask the device itself, and nudge the watchdog while we're here.
    if (device.value?.state !== Device.State.Registered) {
      console.warn(`Dial blocked: device is "${device.value?.state ?? 'missing'}", not registered`)
      toast.error('Your phone is still connecting. Please try again in a moment.')
      armSignalingWatchdog()
      return
    }

    console.log('Making outbound call')
    isMakingCall.value = true

    // Set once the server has created the call, and cleared again once the
    // Twilio leg is up. While it holds an id, this dial owns a call row that
    // nothing else will clean up — so every failure path below has to release
    // it (see the catch).
    let pendingCallId: number | undefined

    // On Telnyx, the phone holds its next ring for this dial until the dial
    // either connects or fails.
    const telnyxDevice =
      currentProvider() === 'telnyx' ? (toRaw(device.value) as unknown as TelnyxDevice) : null
    let connected = false

    try {
      telnyxDevice?.expectDial()

      const response = await axios.post('/api/v1/calls/twilio/outbound', {
        // Blank names are omitted, not sent. Lead::firstOrUpdate treats any key
        // that is merely *set* as an instruction to overwrite, and isset('') is
        // true in PHP — so posting a blank name blanks the lead's real one.
        // undefined would be dropped by JSON.stringify anyway; '' would not,
        // and the New Call tab produces exactly that when a typed name is
        // cleared, because v-model on a text input gives '' rather than null.
        //
        // Trimmed, because whitespace is not a name: '   ' is truthy and would
        // sail through, and the server stores it verbatim. The lead then reads
        // as blank everywhere, since `label` falls back to the "Lead #245"
        // placeholder once full_name trims to nothing.
        ...(fname.value?.trim() ? { fname: fname.value.trim() } : {}),
        ...(lname.value?.trim() ? { lname: lname.value.trim() } : {}),
        phone_number: phone_number.value,
        agent_id: agent_id.value,
        interface: 'desktop',
        // On Telnyx the backend rings this phone for the call, and connect()
        // below answers that ring (CAL-78). Only a phone logged in to Telnyx
        // can take it.
        ...(currentProvider() === 'telnyx' ? { supports_telnyx: 1 } : {}),
      })

      if (!response.data.success) {
        error_message.value = response.data.error
        return
      }

      pendingCallId = response.data.call?.id

      // A phone on Telnyx can't place a Twilio call: the rep's team still
      // sends its calls to Twilio while their account's apps are on Telnyx.
      if (currentProvider() === 'telnyx' && response.data.provider !== 'telnyx') {
        throw new Error(`Phone is on Telnyx but the call went to ${response.data.provider}`)
      }

      openTab.value = null
      agent_id.value = null
      await getCallinglyCall(response.data.call_user_id)
      lead.value = response.data.call.lead

      setAudioDevices()

      const _call = await device.value?.connect({
        params: {
          call_user_id: response.data.call_user_id,
          caller_id: '' + caller_id.value,
          from: 'from',
          To: response.data.call.phone_number,
        },
      })

      // connect() resolves undefined when the device went away between the
      // readiness check above and here. Previously this fell through to
      // setupCallHandlers() with the old twilioCall still in place, leaving the
      // rep on a dead screen and the call row stranded — treat it as the
      // failure it is.
      if (!_call) {
        throw new Error('Twilio device returned no call')
      }

      // The leg is up. From here the call is live and the webhook owns it:
      // OutboundController::dial stamps call_sid, so it can no longer strand.
      pendingCallId = undefined
      connected = true

      twilioCall.value = markRaw(_call)
      acceptCall()
      message.value = 'Dialing...'

      if (call_user.value?.type === 'listen') {
        mute()
      }

      setupCallHandlers()
    } catch (err) {
      console.error(err)

      if (pendingCallId) {
        Sentry.captureException(err)
        toast.error('Could not connect your phone to this call. Please try again.')

        // Deliberately not awaited. Releasing the call server-side is
        // best-effort cleanup, and the same connectivity failure that broke
        // connect() can leave this request pending; holding reset() and
        // isMakingCall on it would strand the rep on a dead call screen with no
        // way to redial — the exact state this path exists to get them out of.
        // The server's 2-minute sweep is the backstop if it never lands.
        void abandonOutboundCall(pendingCallId)
        reset()
      }
    } finally {
      if (!connected) telnyxDevice?.cancelDial()
      isMakingCall.value = false
    }
  }

  const setupCallHandlers = () => {
    if (!twilioCall.value) {
      console.error('setupCallHandlers failed! No twilioCall value.')
      return
    }

    console.log('Setting up call handlers...')

    twilioCall.value.setMaxListeners(8)

    twilioCall.value.on('cancel', () => {
      reset()
    })

    // Media or signaling dropped mid-call and the SDK is trying to recover
    // (ICE restart / signaling reconnect). Drive the ReconnectionIndicator so
    // the agent knows the silence is a network blip, not a dead call.
    twilioCall.value.on('reconnecting', (error) => {
      console.warn('Call reconnecting...', error?.code)
      connectionStore.isReconnecting = true
      errorsStore.addError(error)
    })

    twilioCall.value.on('reconnected', () => {
      console.log('Call reconnected')
      connectionStore.isReconnecting = false
    })

    // Network-quality thresholds crossed (high-rtt, high-jitter,
    // high-packet-loss, low-mos, ...). Cleared counterparts remove them.
    twilioCall.value.on('warning', (name: string) => {
      console.warn('Call quality warning:', name)
      connectionStore.addWarning(name)
    })

    twilioCall.value.on('warning-cleared', (name: string) => {
      console.log('Call quality warning cleared:', name)
      connectionStore.clearWarning(name)
    })

    twilioCall.value.on('disconnect', async () => {
      connectionStore.resetCallHealth()
      await device.value?.audio?.unsetInputDevice()
      isDialpadOpen.value = false
      isMuted.value = false

      if (twilioCall.value && twilioCall.value.status() !== 'closed') {
        axios.post('/twilio/debug/webrtc', {
          user: user.value,
          conn_status: twilioCall.value.status(),
        })
      }

      if (timer.value) {
        clearInterval(timer.value)
      }
      message.value = 'Call Ended'

      if (
        !call.value?.id ||
        call_user.value?.type === 'listen' ||
        call_user.value?.type === 'whisper'
      ) {
        // No call to edit, or the rep was only monitoring someone else's call.
        status.value = 'thankyou'
      } else if (call.value.direction === 'inbound') {
        // Inbound calls skip the call result but still get tags & stage.
        status.value = 'disposition2'
      } else {
        status.value = 'disposition'
      }

      await refreshtoken()

      // Resume the rebuild that handleOnlineStatusChange deferred: when the
      // online event fired mid-call we skipped re-registering to protect the
      // recovering call, and if that recovery failed there is no later online
      // or unregistered event to retry. The device sits in 'unregistered'
      // (register() is only legal in that state), and refreshtoken() above has
      // already given it a fresh token.
      if (online.value && !registered.value && device.value?.state === Device.State.Unregistered) {
        console.log('Call ended with the device unregistered — re-registering...')
        device.value.register().catch((error) => {
          // Usually a stale token: refreshtoken() retries transient failures on
          // a detached timer, and its success path re-registers a dormant
          // device, so this rejection is recovered by the refresh above (or by
          // the device 'error' handler's token paths, 31205/20101/20104).
          console.error('Post-call re-register failed:', error)
        })
      }
    })

    twilioCall.value.on('error', (error) => {
      toast.error(`Error during call. Code: ${error.code}`)
      errorsStore.addError(error)

      // Always capture here, benign code or not. Unlike the device handler
      // above, this event is call-scoped: everything that reaches it ends the
      // call (this handler resets), and status() can't tell an already-doomed
      // call apart from a live one — the SDK's fatal media path runs
      // _disconnect() *before* it emits, so the call reads 'closed' by the time
      // we see the error. The only benign-set code that gets here in practice
      // is 53405, and it arrives on exactly that path: a dropped call, worth
      // its own event.
      Sentry.captureException(error)
      reset()
    })

    twilioCall.value.on('volume', (inputVolume: number, outputVolume: number) => {
      audio.value.inputVolume = inputVolume * 10
      audio.value.outputVolume = outputVolume * 20
    })
  }

  const checkVersion = () => {
    console.log('Checking version...')
    fetch('/version?t=' + Date.now())
      .then((response) => response.text())
      .then((remoteVersion) => {
        const remoteVersionParts = remoteVersion.split('.')
        const localVersionParts = version.split('.')

        let newVersion = false
        for (let i = 0; i < remoteVersionParts.length; i++) {
          if (Number(remoteVersionParts[i]) > Number(localVersionParts[i])) {
            newVersion = true
            break
          }
        }

        if (newVersion) {
          console.log(`A new version ${remoteVersion} is available.`)
          window.location.reload()
        } else {
          console.log(`You are running the latest version ${version}`)
        }
      })
  }

  const handleDoneClick = () => {
    reset()
    checkVersion()
  }

  return {
    // state
    agent_id,
    call,
    call_user,
    call_user_id,
    error_message,
    message,
    note,
    seconds,
    timer,
    lead,
    isMuted,
    isOnHold,
    isDialpadOpen,
    isTransferOpen,
    isTransferStarted,
    isMakingCall,
    openTab,
    showCallConfirm,
    callConfirmLead,
    autoaccept,
    twilioCall,
    dispositionCountdown,
    dispositionTimer,
    DISPOSITION_SECONDS_TO_WAIT,
    // dialer fields (re-exposed from useDialer for convenience)
    getCallerIds,
    getAgents,
    // methods
    ping,
    disconnect,
    startPingInterval,
    stopPingInterval,
    startPhone,
    refreshtoken,
    disarmSignalingWatchdog,
    getAudioPermissions,
    setupDeviceHandlers,
    reset,
    handleOnlineStatusChange,
    acceptCall,
    reject,
    hangup,
    mute,
    unmute,
    digitPress,
    goHome,
    toggleHistory,
    toggleSms,
    toggleNewCall,
    confirmCallLead,
    cancelCallConfirm,
    confirmAndCall,
    toggleDialpad,
    toggleTransfer,
    timerFunction,
    startConference,
    startTransfer,
    startListen,
    startWhisper,
    startBarge,
    hold,
    unhold,
    thankyou,
    disposition,
    saveNote,
    skipNote,
    retry,
    getCallinglyCall,
    incomingLead,
    makeOutboundCall,
    setupCallHandlers,
    checkVersion,
    handleDoneClick,
  }
}
